<div class="patient-analysis">
  <div class="section-header">
    <h2 class="section-title">Analisi Appuntamenti</h2>
    <div class="header-actions">
      <button
        mat-icon-button
        (click)="openSettings()"
        aria-label="Settings"
        class="settings-button"
      >
        <mat-icon>settings</mat-icon>
      </button>
      <button
        mat-icon-button
        (click)="toggleInfoPanel()"
        aria-label="Info"
        class="info-button"
      >
        <mat-icon>info</mat-icon>
      </button>
    </div>
  </div>

  <!-- Loader Container -->
  @if (isLoading) {
    <div class="loading-container">
      <app-loader [size]="'big'"></app-loader>
    </div>
  }

  <!-- Appointment Analysis Table Container -->
  @if (!isLoading) {
    @if (creditsExhausted) {
      <div class="credits-exhausted-banner" role="status">
        <mat-icon aria-hidden="true">info</mat-icon>
        <p>{{ creditsExhaustedMessage }}</p>
      </div>
    }
    <table
      mat-table
      [dataSource]="pastAppointmentsDataSource"
      class="appointment-pipeline-table"
      matSort
      (matSortChange)="onSortChange($event)"
    >
      <!-- Appointment Title Column Container -->
      <ng-container matColumnDef="title">
        <th class="table-header" mat-header-cell *matHeaderCellDef>Titolo</th>
        <td mat-cell *matCellDef="let appointment">
          <span class="appointment-link">
            {{ appointment.title }}
          </span>
        </td>
      </ng-container>
      <!-- Date Column Container -->
      <ng-container matColumnDef="date">
        <th
          class="table-header"
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
        >
          Data
        </th>
        <td mat-cell *matCellDef="let appointment">
          {{ formatDate(appointment.date) }}
        </td>
      </ng-container>
      <!-- Actions Column Container -->
      <ng-container matColumnDef="actions">
        <th class="table-header" mat-header-cell *matHeaderCellDef>Azioni</th>
        <td mat-cell *matCellDef="let appointment">
          @switch (getAppointmentAIPipelineState(appointment)) {
            <!-- Case: File audio not uploaded yet (Action: Upload Audio File) -->
            @case ("NOT_READY_TO_START") {
              <span
                class="upload-action"
                [matTooltip]="uploadActionTooltip(appointment)"
              >
                <button
                  mat-icon-button
                  (click)="triggerFileInput(appointment)"
                  [disabled]="creditsExhausted"
                  class="action-button"
                >
                  <div
                    class="upload-progress-wrapper"
                    [style.display]="
                      isUploading(appointment.id) ? 'flex' : 'none'
                    "
                  >
                    <svg class="progress-ring" width="24" height="24">
                      <circle
                        class="progress-ring__circle"
                        stroke="#4285f4"
                        stroke-width="2"
                        fill="transparent"
                        [attr.r]="radius"
                        r="10"
                        cx="12"
                        cy="12"
                        [attr.stroke-dasharray]="circumference"
                      />
                    </svg>
                    @if (uploadProgress[appointment.id] != null) {
                      <span class="upload-percentage"
                        >{{ uploadProgress[appointment.id] }}%</span
                      >
                    }
                    @if (
                      isUploading(appointment.id) &&
                      uploadProgress[appointment.id] == null
                    ) {
                      <span class="upload-percentage">Err</span>
                    }
                  </div>
                  <mat-icon
                    [style.display]="
                      !isUploading(appointment.id) ? 'inline' : 'none'
                    "
                    >file_upload</mat-icon
                  >
                </button>
              </span>
            }
            <!-- Case: Appointment Pipeline in Progress (Action: Stop the Pipeline) -->
            @case ("IN_PROGRESS") {
              <button
                mat-icon-button
                (click)="deleteAIAppointmentOutputs(appointment)"
                matTooltip="Interrompi analisi AI"
                class="action-button"
              >
                <mat-icon>stop</mat-icon>
              </button>
            }
            <!-- Case: Appointment Pipeline Completed (Action: Show disabled Success Icon) -->
            @case ("SUCCESSFULLY_EXECUTED") {
              <mat-icon
                [matTooltip]="'Analisi AI completata'"
                class="green-action-button"
              >
                check_circle
              </mat-icon>
            }
          }
        </td>
      </ng-container>
      <!-- AI Services Columns Container -->
      <ng-container matColumnDef="aiState">
        <th class="table-header" mat-header-cell *matHeaderCellDef>Stato</th>
        <td mat-cell *matCellDef="let appointment">
          <!-- AI Services Columns-->
          <div class="ai-analysis-icons">
            <!-- Transcription Icon (hourglass while pipeline is starting) -->
            @if (isPipelineAwaitingServices(appointment)) {
              <mat-icon
                class="icon-blue pulsing"
                matTooltip="Analisi in avvio..."
                >hourglass_top</mat-icon
              >
            } @else {
              <mat-icon
                [ngClass]="getIconClass(appointment.transcription_state)"
                matTooltip="Trascrizione"
                >record_voice_over</mat-icon
              >
            }
            <!-- Appointment Summary Icon -->
            <mat-icon
              [ngClass]="
                getIconClass(appointment.appsumm_state).includes('green')
                  ? getIconClass(appointment.appsumm_state) + '-pointer'
                  : getIconClass(appointment.appsumm_state)
              "
              matTooltip="Sintesi Colloquio"
              (click)="navigateTo('summary', appointment)"
              >summarize</mat-icon
            >
            <!-- Appointment Wordcloud Icon -->
            <mat-icon
              [ngClass]="
                getIconClass(appointment.appwordcloud_state).includes('green')
                  ? getIconClass(appointment.appwordcloud_state) + '-pointer'
                  : getIconClass(appointment.appwordcloud_state)
              "
              matTooltip="Wordcloud"
              (click)="navigateTo('wordcloud', appointment)"
              >cloud</mat-icon
            >
            <!-- Appointment Topic Distribution Icon -->
            <mat-icon
              [ngClass]="
                getIconClass(appointment.catdistr_state).includes('green')
                  ? getIconClass(appointment.catdistr_state) + '-pointer'
                  : getIconClass(appointment.catdistr_state)
              "
              matTooltip="Distribuzione Topic Nucleari"
              (click)="navigateTo('nuclear-topic', appointment)"
              >assessment</mat-icon
            >
            <!-- Appointment Topics Icon -->
            <mat-icon
              [ngClass]="
                getIconClass(appointment.apptopics_state).includes('green')
                  ? getIconClass(appointment.apptopics_state) + '-pointer'
                  : getIconClass(appointment.apptopics_state)
              "
              matTooltip="Topic AI"
              (click)="navigateTo('aitopic', appointment)"
              >topic</mat-icon
            >
          </div>
        </td>
      </ng-container>
      <!-- Navigate to Appointment Results Button -->
      <ng-container matColumnDef="navigateToAppointment">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let appointment">
          @if (areAllServicesComplete(appointment)) {
            <div class="action-container">
              <app-clinease-button
                [text]="'Vai a Risultati'"
                (clicked)="navigateToAppointment(appointment)"
              >
              </app-clinease-button>
            </div>
          }
        </td>
      </ng-container>
      <!-- More Actions Button -->
      <ng-container matColumnDef="moreActions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let appointment">
          @if (appointment.ai_pipeline_state !== "NOT_READY_TO_START") {
            <button
              mat-icon-button
              [matMenuTriggerFor]="menu"
              aria-label="More actions"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
          }
          <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="copyAppointmentId(appointment)">
              <mat-icon>content_copy</mat-icon>
              <span>Copia ID appuntamento</span>
            </button>
            <button
              mat-menu-item
              (click)="deleteAIAppointmentOutputs(appointment)"
            >
              <mat-icon>delete</mat-icon>
              <span>Cancella risultati</span>
            </button>
            @if (appointment.ai_pipeline_state === "SUCCESSFULLY_EXECUTED") {
              <button
                mat-menu-item
                (click)="downloadTranscription(appointment)"
              >
                <mat-icon>download</mat-icon>
                <span>Scarica trascrizione</span>
              </button>
            }
          </mat-menu>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
    </table>
  }
</div>
<input
  type="file"
  #fileInput
  style="display: none"
  (change)="onFileSelected($event)"
  accept="audio/mp3,audio/mpeg,audio/mp4,audio/x-m4a"
/>

@if (showInfoPanel) {
  <app-info-panel
    [infoTitle]="infoTitle"
    [infoText]="infoText"
    (close)="toggleInfoPanel()"
  ></app-info-panel>
}
